import { TcdlObjectRef, TcdlToken, objectReferences, tokenizeLine } from './tcdl-tokenizer';

/** A library while its answer is pending, when the project has none of that name, or its entry names. */
export type LibraryState = 'loading' | 'missing' | ReadonlySet<string>;

export type TcdlFindingKind = 'unknown-command' | 'not-implemented' | 'unknown-library' | 'unknown-entry';

export interface TcdlFinding {
  readonly kind: TcdlFindingKind;
  /** Column range of the marked part in the line. */
  readonly start: number;
  readonly length: number;
  readonly command?: string;
  readonly library?: string;
  readonly entry?: string;
}

/** Every library the lines refer to, once, in order of appearance. */
export function collectLibraries(lines: readonly string[]): string[] {
  return [...new Set(lines.flatMap((line) => objectReferences(line).map((ref) => ref.library.text)))];
}

/**
 * What a line gets marked for: first its command, then its objects from left to right.
 * Names compare exactly, as in the backend; a library still loading marks nothing.
 */
export function findingsFor(
  text: string,
  lookup: (library: string) => LibraryState,
  tokens: readonly TcdlToken[] = tokenizeLine(text),
  refs: readonly TcdlObjectRef[] = objectReferences(text, tokens),
): TcdlFinding[] {
  const findings: TcdlFinding[] = [];
  const commandIndex = tokens.findIndex((t) => t.kind === 'command');
  const command = tokens[commandIndex];
  if (command && command.status !== 'known') {
    const start = tokens.slice(0, commandIndex).reduce((sum, t) => sum + t.text.length, 0);
    findings.push({
      kind: command.status === 'unknown' ? 'unknown-command' : 'not-implemented',
      start,
      length: command.text.length,
      command: command.text,
    });
  }
  for (const ref of refs) {
    const state = lookup(ref.library.text);
    if (state === 'loading') continue;
    const library = ref.library.text;
    const entry = ref.entry.text;
    if (state === 'missing') {
      findings.push({ kind: 'unknown-library', start: ref.library.start, length: ref.library.length, library, entry });
    } else if (!state.has(entry)) {
      findings.push({ kind: 'unknown-entry', start: ref.entry.start, length: ref.entry.length, library, entry });
    }
  }
  return findings;
}
